import React from 'react';
import _ from 'lodash';

import {
  CustomerOrderActionsWidgetDocument,
  InitiateReturnAuthorizationInput,
  ReturnAuthorizationLineItemInput,
  SingleOrderRecordNavigatorDocument,
  useInitiateReturnAuthorizationAsCustomerMutation,
  useInitiateReturnAuthorizationMutation,
  useInitiateReturnModalQuery
} from 'generated/graphql';

import { NetSuiteRecordChip } from 'modules/layer00';
import Big from 'big.js';
import * as types from './types';
import { DateTime } from 'luxon';



export interface INonSerializedLineItem {
  type: 'nonSerialized';
  id: string;
  sku: string;
  productName: string;
  quantity: number;
  lineTotalDollars: Big;
}

export interface ISerializedLineItem {
  type: 'serialized';
  id: string;
  sku: string;
  productName: string;
  quantity: number;
  availableSerialNumbers: string[];
  lineTotalDollars: Big;
}

export type ILineItem = INonSerializedLineItem | ISerializedLineItem;


export interface IOrder {
  customerId: string;
  customerFullName: string;
  customerWriteStatus: NetSuiteRecordChip.IWriteStatus;

  storeId: string;
  orderNumber: string;
  writeStatus: NetSuiteRecordChip.IWriteStatus;
  orderDate: DateTime;
  itemFulfillmentWriteStatus: NetSuiteRecordChip.IWriteStatus;
}

export interface IData {
  isSmartPayOrder: boolean;
  lineItems: ILineItem[];
  order: IOrder;
}

export function useQuery(orderId: string, asCustomer: boolean) {
  const tuple = useInitiateReturnModalQuery({
    variables: {
      orderId,
      asCustomer
    }
  });

  return React.useMemo(
    () => {
      const unifiedLineItems = asCustomer
        ? _.map(tuple.data?.orderLite?.lineItems ?? [], lineItem => ({
          id: lineItem.id,
          sku: lineItem.sku,
          productName: lineItem.productName,
          quantity: lineItem.quantity,
          lineTotalDollars: __genDefaultAmountToRefund({
            feeTotalCents: new Big(lineItem.feeTotalCents),
            itemTotalCents: new Big(lineItem.itemTotalCents),
            discountTotalCents: new Big(lineItem.discountTotalCents),
            taxTotalCents: new Big(lineItem.taxTotalCents)
          })
        }))
        : _.map(tuple.data?.order?.lineItems ?? [], lineItem => ({
          id: lineItem.id,
          sku: lineItem.product.sku,
          productName: lineItem.product.name,
          quantity: lineItem.quantity,
          lineTotalDollars: __genDefaultAmountToRefund({
            feeTotalCents: new Big(lineItem.feeTotalCents),
            itemTotalCents: new Big(lineItem.itemTotalCents),
            discountTotalCents: new Big(lineItem.discountTotalCents),
            taxTotalCents: new Big(lineItem.taxTotalCents)
          })
        }));

      const adminSerialNumbers = _.map(
        tuple.data?.order?.itemFulfillment?.lineItems ?? [],
        lineItem => ({
          sku: lineItem.item.sku,
          serialNumbers: _.map(lineItem.assignments, assignment => assignment.inventoryNumber)
        }));
      const customerSerialNumbers = _.map(
        tuple.data?.orderLite?.itemFulfillment?.lineItems ?? [],
        lineItem => ({
          sku: lineItem.item.sku,
          serialNumbers: _.map(lineItem.assignments, assignment => assignment.inventoryNumber)
        }));

      const lineItemsWithSerialNumbers = _.filter(
        asCustomer ? customerSerialNumbers : adminSerialNumbers,
        row => !_.isEmpty(row.serialNumbers)
      );

      // Calculates what serial numbers are not yet returned, and should show up in the UI.
      const receivedSerialNumbers = _.compact(asCustomer ?
        _.flatMap(
          tuple.data?.orderLite?.returnAuthorizations,
          ra => ra.receivedInventoryNumbers
        ) :
        _.flatMap(
          tuple.data?.order?.returnAuthorizations.nodes ?? [],
          rma => _.map(rma.itemReceipt?.lineItems, lineItem => lineItem.inventoryNumber)
        )
      );

      const sku2ReturnableSerialNumbers = _.mapValues(
        _.groupBy(lineItemsWithSerialNumbers, value => value.sku),
        value => _.filter(_.flatMap(value, v => v.serialNumbers), sn => !_.includes(receivedSerialNumbers, sn))
      );

      const allLineItems = _.map(
        unifiedLineItems,
        (lineItem): ILineItem => _.has(sku2ReturnableSerialNumbers, lineItem.sku) ? ({
          type: 'serialized',
          id: lineItem.id,
          sku: lineItem.sku,
          productName: lineItem.productName,
          lineTotalDollars: lineItem.lineTotalDollars,
          quantity: lineItem.quantity,
          availableSerialNumbers: sku2ReturnableSerialNumbers[lineItem.sku]
        }) : ({
          type: 'nonSerialized',
          id: lineItem.id,
          sku: lineItem.sku,
          productName: lineItem.productName,
          lineTotalDollars: lineItem.lineTotalDollars,
          quantity: lineItem.quantity
        }));

      const data: IData = {
        isSmartPayOrder: (tuple.data?.order?.paymentMethod.name ?? tuple.data?.orderLite?.paymentMethod.name) === 'smartPay',
        order: {
          orderDate: DateTime.fromISO(tuple.data?.order?.orderDate ?? tuple.data?.orderLite?.orderDate ?? ''),
          customerFullName: tuple.data?.order?.customer.fullName ?? tuple.data?.orderLite?.customerFullName ?? '',
          customerId: tuple.data?.order?.customer.id ?? '',
          customerWriteStatus: NetSuiteRecordChip.genWriteStatus({
            netSuiteRecord: tuple.data?.order?.customer.netSuiteRecord,
            writeRequest: tuple.data?.order?.customer.netSuiteWriteRequest
          }),
          orderNumber: tuple.data?.order?.orderNumber ?? tuple.data?.orderLite?.orderNumber ?? '',
          storeId: tuple.data?.order?.store.id ?? '',
          writeStatus: NetSuiteRecordChip.genWriteStatus({
            netSuiteRecord: tuple.data?.order?.netSuiteRecord,
            writeRequest: tuple.data?.order?.netSuiteWriteRequest
          }),
          itemFulfillmentWriteStatus: NetSuiteRecordChip.genWriteStatus({
            netSuiteRecord: tuple.data?.order?.itemFulfillment?.netSuiteRecord
          })
        },
        lineItems: _.filter(
          allLineItems,
          li => li.type === 'serialized' && !_.isEmpty(li.availableSerialNumbers) || li.type === 'nonSerialized'
        )
      };

      return {
        ...tuple,
        data
      };
    },
    [
      tuple,
      asCustomer
    ]
  );
}



function __genDefaultAmountToRefund({
  feeTotalCents, itemTotalCents, discountTotalCents, taxTotalCents
}: {
  feeTotalCents: Big;
  itemTotalCents: Big;
  discountTotalCents: Big;
  taxTotalCents: Big;
}) {
  return new Big(itemTotalCents)
    .plus(feeTotalCents)
    .plus(taxTotalCents)
    .minus(discountTotalCents)
    .div(100);
}



export function useInitiateReturnAuthorization(orderId: string) {
  const [fnMutate, tuple] = useInitiateReturnAuthorizationMutation({
    refetchQueries: [
      {
        query: SingleOrderRecordNavigatorDocument,
        variables: {
          orderId
        }
      }
    ]
  });

  const fnWrappedMutate = React.useCallback(
    async (formState: types.IFormState) => {
      const result = await fnMutate({
        variables: {
          input: __formState2Input(orderId, formState)
        }
      });

      if (result.data && _.isNil(result.data.initiateReturnAuthorization)) {
        window.alert("The return authorization was unable to be created. This may be due to an error in NetSuite, or the order was not fulfilled in E1.");
      }
    },
    [
      fnMutate,
      orderId
    ]
  );

  return React.useMemo(
    () => [
      fnWrappedMutate,
      {
        ...tuple,
        data: null
      }
    ] as const,
    [
      fnWrappedMutate,
      tuple
    ]);
}



export function useInitiateReturnAuthorizationAsCustomer(orderId: string) {
  const [fnMutate, tuple] = useInitiateReturnAuthorizationAsCustomerMutation({
    refetchQueries: [
      {
        query: SingleOrderRecordNavigatorDocument,
        variables: {
          orderId
        }
      },
      {
        query: CustomerOrderActionsWidgetDocument,
        variables: {
          orderId
        }
      }
    ]
  });

  const fnWrappedMutate = React.useCallback(
    async (formState: types.IFormState) => {
      const result = await fnMutate({
        variables: {
          input: __formState2Input(orderId, formState)
        }
      });

      if (result.data && _.isNil(result.data.initiateReturnAuthorizationAsCustomer)) {
        window.alert("The return authorization was unable to be created. Please contact support.");
      } else {
        window.alert("Return authorization created successfully.");
      }
    },
    [
      fnMutate,
      orderId
    ]
  );

  return React.useMemo(
    () => [
      fnWrappedMutate,
      {
        ...tuple,
        data: null
      }
    ] as const,
    [
      fnWrappedMutate,
      tuple
    ]
  );
}



function __formState2Input(orderId: string, formState: types.IFormState): InitiateReturnAuthorizationInput {
  return {
    orderId,
    initialNote: formState.initialNote,
    lineItems: _.map(formState.lineItems, (li): ReturnAuthorizationLineItemInput => ({
      lineItemId: li.lineItemId,
      reasonForReturn: li.reasonForReturn?.id ?? '',
      quantity: _.toNumber(li.quantity),
      refundAmountCents: new Big(li.refundAmountDollars).mul(100).toString(),
      serialNumber: li.serialNumber
    }))
  };
}
